import { useEffect, useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { fetchAvailableProducts, fetchSellingTypes, fetchMaterialColors, fetchWireTypes, reviewPrice, createSell } from '@/services/products'
import { fetchCustomers } from '@/services/customers'
import type { AvailableProduct, ProductQuantity, SellingType, MaterialColor, WireType } from '@/types/products'
import type { Customer } from '@/types/customers'
import { AlertCircle } from 'lucide-react'

interface CreateSellSheetProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  selectedProduct?: ProductQuantity | null
  onSuccess?: () => void
  showCustomerPicker?: boolean
  showSellingTypePicker?: boolean
}

interface PricePreview {
  text: number[]
  profit: number
  calculated_price: number
}

export function CreateSellSheet({
  open,
  onOpenChange,
  selectedProduct: initialProduct,
  onSuccess,
  showCustomerPicker = false,
  showSellingTypePicker = false,
}: CreateSellSheetProps) {
  const { t, i18n } = useTranslation()
  const [availableProducts, setAvailableProducts] = useState<AvailableProduct[]>([])
  const [isLoadingProducts, setIsLoadingProducts] = useState(false)
  const [selectedProductIndex, setSelectedProductIndex] = useState<string>('')
  const [quantityInput, setQuantityInput] = useState('')
  const [profitInput, setProfitInput] = useState('')
  const [isCreating, setIsCreating] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [pricePreview, setPricePreview] = useState<PricePreview | null>(null)
  const [customerGivenInput, setCustomerGivenInput] = useState('')

  // Selling type state
  const [sellingTypes, setSellingTypes] = useState<SellingType[]>([])
  const [selectedSellingTypeId, setSelectedSellingTypeId] = useState<string>('')

  // Customer picker state
  const [customers, setCustomers] = useState<Customer[]>([])
  const [clientPhone, setClientPhone] = useState('')
  const [isClientDropdownOpen, setIsClientDropdownOpen] = useState(false)

  // Colors and types state
  const [materialColors, setMaterialColors] = useState<MaterialColor[]>([])
  const [wireTypes, setWireTypes] = useState<WireType[]>([])

  // Determine effective selected product
  const effectiveProduct = useMemo(() => {
    if (initialProduct) return initialProduct
    if (selectedProductIndex !== '') {
      const index = parseInt(selectedProductIndex, 10)
      return availableProducts[index] || null
    }
    return null
  }, [initialProduct, selectedProductIndex, availableProducts])

  // Load products and customers on open
  useEffect(() => {
    if (open) {
      // Reset state
      if (!initialProduct) {
        setSelectedProductIndex('')
      }
      setQuantityInput('')
      setProfitInput('')
      setClientPhone('')
      setError(null)
      setPricePreview(null)
      setCustomerGivenInput('')

      // Load data
      const loadData = async () => {
        setIsLoadingProducts(true)
        try {
          const [productsData, colorsData, typesData] = await Promise.all([
            fetchAvailableProducts(),
            fetchMaterialColors(),
            fetchWireTypes(),
          ])
          setAvailableProducts(productsData)
          setMaterialColors(colorsData)
          setWireTypes(typesData)

          if (showCustomerPicker) {
            const customersData = await fetchCustomers()
            setCustomers(customersData)
          }

          if (showSellingTypePicker) {
            const types = await fetchSellingTypes()
            setSellingTypes(types)
            const firstType = types.find(t => t.id != null)
            if (firstType) {
              setSelectedSellingTypeId(String(firstType.id))
            }
          }
        } catch {
          setError(t('sell.loadProductsFailed'))
        } finally {
          setIsLoadingProducts(false)
        }
      }
      loadData()
    }
  }, [open, initialProduct, showCustomerPicker, showSellingTypePicker, t])

  // Filter customers based on search
  const filteredCustomers = useMemo(() => {
    if (!clientPhone.trim()) return customers
    const search = clientPhone.toLowerCase()
    return customers.filter(
      (c) =>
        c.phone_number?.toLowerCase().includes(search) ||
        c.full_name?.toLowerCase().includes(search)
    )
  }, [customers, clientPhone])

  const locale = i18n.language === 'ru' ? 'ru-RU' : 'uz-UZ'

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat(locale, {
      maximumFractionDigits: 2,
    }).format(num)
  }

  const formatCurrency = (num: number) => {
    return '$' + formatNumber(num)
  }

  // Get color ID by name
  const getColorIdByName = (name: string | undefined): number | undefined => {
    if (!name) return undefined
    const color = materialColors.find(c => c.name === name)
    return color?.id
  }

  // Get type ID by name
  const getTypeIdByName = (name: string | undefined): number | undefined => {
    if (!name) return undefined
    const type = wireTypes.find(t => t.name === name)
    return type?.id
  }

  // Load price preview when product, quantity, or profit changes
  useEffect(() => {
    const loadPricePreview = async () => {
      if (!effectiveProduct || !quantityInput.trim()) {
        setPricePreview(null)
        return
      }

      const quantity = parseFloat(quantityInput)
      if (isNaN(quantity) || quantity <= 0) {
        setPricePreview(null)
        return
      }

      const profit = profitInput.trim() ? parseFloat(profitInput) : 0
      if (profitInput.trim() && isNaN(profit)) {
        setPricePreview(null)
        return
      }

      const texts = [
        effectiveProduct.thickness,
        effectiveProduct.cell,
        effectiveProduct.height,
        effectiveProduct.width,
        quantity,
      ]

      const selling_type_id = showSellingTypePicker && selectedSellingTypeId ? parseInt(selectedSellingTypeId, 10) : null

      try {
        const preview = await reviewPrice({ text: texts, profit, selling_type: selling_type_id })
        setPricePreview(preview)
        setError(null)
      } catch (err) {
        const axiosError = err as { response?: { status?: number } }
        if (axiosError.response?.status === 500) {
          setError(t('sell.productNotInSellingType'))
        }
        setPricePreview(null)
      }
    }

    loadPricePreview()
  }, [effectiveProduct, quantityInput, profitInput])

  const handleCreate = async () => {
    if (!effectiveProduct || !quantityInput.trim()) return

    const quantity = parseFloat(quantityInput)
    if (isNaN(quantity) || quantity <= 0) {
      setError(t('sell.invalidQuantity'))
      return
    }

    const profit = profitInput.trim() ? parseFloat(profitInput) : 0
    if (profitInput.trim() && isNaN(profit)) {
      setError(t('sell.invalidProfit'))
      return
    }

    const texts = [
      effectiveProduct.thickness,
      effectiveProduct.cell,
      effectiveProduct.height,
      effectiveProduct.width,
      quantity,
    ]

    setIsCreating(true)
    setError(null)

    try {
      const customer_given_price = customerGivenInput.trim() ? parseFloat(customerGivenInput) : null
      const selling_type_id = showSellingTypePicker && selectedSellingTypeId ? parseInt(selectedSellingTypeId, 10) : null
      await createSell({
        texts,
        profit,
        client: showCustomerPicker && clientPhone.trim() ? clientPhone.trim() : undefined,
        customer_given_price: customer_given_price ?? undefined,
        selling_type: selling_type_id ?? undefined,
        color: getColorIdByName(effectiveProduct.color__name),
        type: getTypeIdByName(effectiveProduct.type__name),
      })
      onOpenChange(false)
      onSuccess?.()
    } catch (err) {
      const axiosError = err as { response?: { data?: { data?: { texts?: string } } } }
      const errorMessage = axiosError.response?.data?.data?.texts

      if (errorMessage === 'Insufficient stock weight for the specified product.') {
        setError(t('sell.insufficientStockWeight'))
      } else if (errorMessage === 'Insufficient stock quantity for the specified product.') {
        setError(t('sell.insufficientStockQuantity'))
      } else {
        setError(t('sell.createFailed'))
      }
    } finally {
      setIsCreating(false)
    }
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className='px-4 overflow-y-auto'>
        <SheetHeader>
          <SheetTitle>{t('sell.createSale')}</SheetTitle>
          <SheetDescription>{t('sell.createDescription')}</SheetDescription>
        </SheetHeader>

        <div className='space-y-6 py-6'>
          {/* Product selector (only if no pre-selected product) */}
          {!initialProduct && (
            <div className='space-y-2'>
              <Label>{t('sell.selectProduct')}</Label>
              {isLoadingProducts ? (
                <Skeleton className='h-10 w-full' />
              ) : availableProducts.length === 0 ? (
                <div className='rounded-md border border-dashed p-4 text-center text-sm text-muted-foreground'>
                  {t('sell.noProductsAvailable')}
                </div>
              ) : (
                <Select
                  value={selectedProductIndex}
                  onValueChange={setSelectedProductIndex}
                >
                  <SelectTrigger>
                    <SelectValue
                      placeholder={t('sell.selectProductPlaceholder')}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {availableProducts.map((product, index) => (
                      <SelectItem key={index} value={String(index)}>
                        {product.thickness} • {product.cell} • {product.height}×{product.width} • {product.color__name} • {product.type__name} ({t('sell.available')}: {product.total_count})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          )}

          {/* Selected product display (only if pre-selected product) */}
          {initialProduct && effectiveProduct && (
            <div className='rounded-lg border bg-muted/50 p-4'>
              <div className='text-sm font-medium mb-2'>{t('sell.selectedProduct')}</div>
              <div className='text-sm text-muted-foreground space-y-1'>
                <div>{effectiveProduct.thickness}mm • {t('sell.cell')}: {effectiveProduct.cell} • {effectiveProduct.height}×{effectiveProduct.width}m</div>
                {(effectiveProduct.color__name || effectiveProduct.type__name) && (
                  <div className='text-xs'>
                    {effectiveProduct.color__name && <span>{effectiveProduct.color__name}</span>}
                    {effectiveProduct.color__name && effectiveProduct.type__name && <span> • </span>}
                    {effectiveProduct.type__name && <span>{effectiveProduct.type__name}</span>}
                  </div>
                )}
                <div className='text-xs mt-1'>
                  {t('sell.available')}: {effectiveProduct.total_count}
                </div>
              </div>
            </div>
          )}

          {/* Customer picker */}
          {showCustomerPicker && (
            <div className='relative space-y-2'>
              <Label htmlFor='clientPhone'>
                {t('orders.client')} ({t('orders.optional')})
              </Label>
              <Input
                id='clientPhone'
                value={clientPhone}
                onChange={(e) => {
                  const value = e.target.value.replace(/[^\d+]/g, '').slice(0, 13)
                  setClientPhone(value)
                  setIsClientDropdownOpen(true)
                }}
                onFocus={() => setIsClientDropdownOpen(true)}
                onBlur={() => setTimeout(() => setIsClientDropdownOpen(false), 150)}
                placeholder='+998901234567'
                autoComplete='off'
              />
              {isClientDropdownOpen && filteredCustomers.length > 0 && (
                <div className='absolute z-50 mt-1 max-h-48 w-full overflow-auto rounded-md border bg-popover p-1 shadow-md'>
                  {filteredCustomers.map((customer) => (
                    <div
                      key={customer.id}
                      className='cursor-pointer rounded-sm px-2 py-1.5 text-sm hover:bg-accent hover:text-accent-foreground'
                      onMouseDown={(e) => {
                        e.preventDefault()
                        setClientPhone(customer.phone_number || '')
                        setIsClientDropdownOpen(false)
                      }}
                    >
                      {customer.full_name} ({customer.phone_number})
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Selling type picker */}
          {showSellingTypePicker && (
            <div className='space-y-2'>
              <Label>{t('orders.sellingType')}</Label>
              {sellingTypes.length === 0 ? (
                <Skeleton className='h-10 w-full' />
              ) : (
                <Select
                  value={selectedSellingTypeId}
                  onValueChange={setSelectedSellingTypeId}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t('orders.selectSellingType')} />
                  </SelectTrigger>
                  <SelectContent>
                    {sellingTypes.map((type) => (
                      <SelectItem key={type.id} value={String(type.id)}>
                        {type.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          )}

          <div className='space-y-2'>
            <Label htmlFor='quantityInput'>{t('sell.quantity')}</Label>
            <Input
              id='quantityInput'
              type='number'
              min='1'
              step='1'
              value={quantityInput}
              onChange={(e) => setQuantityInput(e.target.value)}
              placeholder={t('sell.quantityPlaceholder')}
            />
          </div>

          <div className='space-y-2'>
            <Label htmlFor='profitInput'>
              {t('sell.profit')} ({t('orders.optional')})
            </Label>
            <Input
              id='profitInput'
              type='number'
              step='0.01'
              value={profitInput}
              onChange={(e) => setProfitInput(e.target.value)}
              placeholder='0.00'
            />
          </div>

          <div className='space-y-2'>
            <Label htmlFor='customerGivenInput'>
              {t('sell.customerGivenPrice')} ({t('orders.optional')})
            </Label>
            <Input
              id='customerGivenInput'
              type='number'
              step='0.01'
              value={customerGivenInput}
              onChange={(e) => setCustomerGivenInput(e.target.value)}
              placeholder='0.00'
            />
          </div>

          {/* Price Preview */}
          {pricePreview && (
            <div className='rounded-lg border bg-muted/50 p-4 space-y-2'>
              <div className='text-sm font-medium'>{t('sell.pricePreview')}</div>
              <div className='grid grid-cols-3 gap-4'>
                <div>
                  <div className='text-xs text-muted-foreground'>{t('common.price')}</div>
                  <div className='font-semibold text-primary'>
                    {formatCurrency(pricePreview.calculated_price)}
                  </div>
                </div>
                <div>
                  <div className='text-xs text-muted-foreground'>{t('sell.profit')}</div>
                  <div className='font-semibold text-green-600 dark:text-green-400'>
                    {formatCurrency(pricePreview.profit)}
                  </div>
                </div>
                <div>
                  <div className='text-xs text-muted-foreground'>{t('sell.inputData')}</div>
                  <div className='font-medium text-sm'>
                    {pricePreview.text.join(' • ')}
                  </div>
                </div>
              </div>
            </div>
          )}

          {error && (
            <div className='flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-400'>
              <AlertCircle className='h-4 w-4 shrink-0' />
              <span>{error}</span>
            </div>
          )}
        </div>

        <SheetFooter>
          <Button
            onClick={handleCreate}
            disabled={isCreating || !effectiveProduct || !quantityInput.trim()}
            className='w-full'
          >
            {isCreating ? t('common.loading') : t('sell.createSale')}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  )
}
